/* eslint-disable no-console */
import { ICache, ICacheResult, IResponse } from '../types';

export class IndexDB implements ICache {
  private db: any;
  private readonly databaseName: string;
  private readonly tableName: string;
  private readonly readOnly = 'readonly';
  private readonly readWrite = 'readwrite';

  private init(onInitDone: (data: string) => void) {
    const request = window.indexedDB.open(this.databaseName, 1);
    if (!request) {
      throw Error('Browser does not support indexedDB feature');
    }

    // existing indexdb
    request.onsuccess = () => {
      this.db = request.result;
      onInitDone('onsuccess');
    };
    // first use
    request.onupgradeneeded = (event: any) => {
      this.db = event?.target?.result;
      if (this.tableName && !this.db.objectStoreNames.contains(this.tableName)) {
        // strings as keys
        this.db.createObjectStore(this.tableName, { autoIncrement: true });
      }
      onInitDone('onupgradeneeded');
    };
  }

  // store in a single collection named as the given tableName parameter
  constructor(databaseName: string, tableName: string, onInitDone: (edata: string) => void) {
    if (!window || !window.indexedDB || typeof window.indexedDB.open !== 'function') {
      throw Error('Browser does not support indexedDB feature');
    }
    this.tableName = tableName;
    this.databaseName = databaseName;
    this.init(onInitDone);
  }

  async upsert<T>(key: string, value: T, tableName?: string): Promise<ICacheResult<T>> {
    let result: ICacheResult<T> = { success: true };
    const table = tableName || this.tableName;

    try {
      const tx = this.db.transaction(table, this.readWrite);
      const store = tx.objectStore(table, { autoIncrement: true });
      result = await new Promise((resolve) => {
        const putR = store.put(value, key);
        putR.onsuccess = () => resolve(putR.result);
      });
    } catch (e) {
      console.error('add error ', e);
      result.success = false;
    }
    return result;
  }

  // delete  key/value pairs of key associated with the given keys
  async delete(key: string, tableName?: string): Promise<ICacheResult<any>> {
    const result: ICacheResult<any> = { success: true };
    const table = tableName || this.tableName;
    try {
      const tx = this.db.transaction(table, this.readWrite);
      const store = tx.objectStore(table);
      store.delete(key);
      const ok = await new Promise((resolve) => {
        tx.oncomplete = () => resolve('ok');
      });
      result.data = ok as any;
    } catch (e) {
      console.error('delete error', e);
      result.success = false;
    }
    return result;
  }

  async get<T>(key: string, tableName?: string): Promise<ICacheResult<T>> {
    const result: ICacheResult<T> = { success: true };
    const table = tableName || this.tableName;
    let value;
    try {
      const tx = this.db.transaction(table, this.readOnly);
      const store = tx.objectStore(table);
      const req = store.get(key);
      value = await new Promise((resolve) => {
        req.onsuccess = () => resolve(req.result);
      });
      result.data = value as T;
    } catch (e) {
      // debugger;
      console.error('get error', e);
      result.success = false;
    }

    return result;
  }

  /**
   * Get all records belong to the given `tableName` or `tableName` in constructor
   * @param tableName the name of table we want to get all records inside of it
   * @returns Promise<ICacheResult>
   */
  async getAllRows<T>(tableName?: string): Promise<ICacheResult<T>> {
    const result: ICacheResult<T> = { success: true };
    const table = tableName || this.tableName;
    try {
      const tx = this.db.transaction(table, this.readOnly);
      const store = tx.objectStore(table);

      const entries = (await new Promise((resolve) => {
        const resp: IResponse = {};
        store.openCursor().onsuccess = (event: any) => {
          // @ts-ignore
          const cursor = event.target.result;
          if (cursor) {
            const key: string = cursor.primaryKey;
            const value = cursor.value;
            resp[key] = value;
            cursor.continue();
          } else {
            // all done
            resolve(resp);
          }
        };
      })) as any;
      result.data = entries as T;
    } catch (e) {
      console.error('get all error', e);
      result.success = false;
    }
    return result;
  }

  /**
   * close connection to indexdb
   */
  async close() {
    this.db.close();
  }
}
